import { Col } from '@amzn/stencil-react-components/layout';
import {
  MessageBanner,
  MessageBannerType
} from '@amzn/stencil-react-components/message-banner';
import React, { useState } from 'react';
import { ErrorDetailsPanel } from 'src/components/ErrorDetailsPanel/ErrorDetailsPanel';
import { ExceptionDetail, toExceptionDetail } from 'src/models/ExceptionDetail';
import { FailureContext } from 'src/utils/ErrorRedirectUtils';

/**
 * Shared inline-error plumbing for the admin console's create/edit/add modals.
 *
 * Every such modal previously hand-rolled the same ~15 lines: an
 * `ExceptionDetail` state, clear-on-submit-start, clear-on-modal-open,
 * capture-in-catch, and a render block showing a friendly red message plus the
 * collapsible {@link ErrorDetailsPanel}. Duplicating that across 13+ call sites
 * repeatedly reintroduced the same bugs (dismissing on failure, stale panel on
 * reopen). This hook centralises the behaviour so a modal just calls
 * `captureError` in its catch, `clearError` on submit-start and on modal-open,
 * and drops `renderError(...)` into its form body.
 *
 * Errors thrown by our builders are already {@link ExceptionDetail}s; TRD/asset
 * APIs rethrow the raw AxiosError. `captureError` normalises both: a value that
 * already carries an `errorCode`/`errorMessage` is kept as-is, otherwise it is
 * run through {@link toExceptionDetail}.
 */
export interface UseInlineErrorResult {
  /** The captured exception, or undefined when there is no active error. */
  errorException: ExceptionDetail | undefined;
  /** Store a caught error (raw Axios or already-parsed) as the active error. */
  captureError: (error: unknown) => void;
  /** Clear the active error (call on submit-start and on modal open). */
  clearError: () => void;
  /**
   * Render the standardised inline error: a friendly red message plus the
   * collapsible ErrorDetailsPanel. Returns null when there is no active error.
   * @param source - `context.source` for the panel, e.g. `'CreateUsersModal - createUser'`.
   * @param fallbackMessage - shown when the exception has no `errorMessage`.
   */
  renderError: (source: string, fallbackMessage: string) => JSX.Element | null;
}

const isExceptionDetail = (error: unknown): error is ExceptionDetail =>
  typeof error === 'object' &&
  error !== null &&
  ('errorCode' in error || 'errorStatus' in error || 'errorMessage' in error);

export const useInlineError = (): UseInlineErrorResult => {
  const [errorException, setErrorException] = useState<
    ExceptionDetail | undefined
  >(undefined);

  const captureError = (error: unknown) => {
    setErrorException(
      isExceptionDetail(error) ? error : toExceptionDetail(error)
    );
  };

  const clearError = () => setErrorException(undefined);

  const renderError = (source: string, fallbackMessage: string) => {
    if (!errorException) {
      return null;
    }
    const context: Partial<FailureContext> = { source };
    return (
      <Col gridGap={'S100'}>
        <MessageBanner type={MessageBannerType.Error}>
          {errorException.errorMessage || fallbackMessage}
        </MessageBanner>
        <ErrorDetailsPanel exception={errorException} context={context} />
      </Col>
    );
  };

  return { errorException, captureError, clearError, renderError };
};

export default useInlineError;
